import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Template } from "@/types/template";
import { ChevronLeft, Download, Upload, CheckCircle, XCircle, FileText } from "lucide-react";
import { format } from "date-fns";
import { TemplateContent } from "@/components/template/TemplateContent";
import { processUploadedTemplate } from "@/utils/templateProcessor";
import "@/styles/TemplateDocument.css";

export default function TemplateReviewPage() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [template, setTemplate] = useState<Template | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [htmlFile, setHtmlFile] = useState<File | null>(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [addToLibrary, setAddToLibrary] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [processedContent, setProcessedContent] = useState<{
    processedHtml: string;
    placeholders: string[];
    displayNames: Record<string, string>;
  } | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    if (!templateId) {
      navigate("/admin/review");
      return;
    }
    fetchTemplate();
  }, [templateId]);

  const fetchTemplate = async () => {
    if (!templateId) return;
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('templates')
        .select('*')
        .eq('id', templateId)
        .single();

      if (error) throw error;
      setTemplate(data as Template);
      
      // Try to fetch admin uploaded HTML first
      if (data.admin_uploaded_html) {
        const { data: htmlData, error: htmlError } = await supabase.storage
          .from('template_html')
          .download(data.admin_uploaded_html);
        if (!htmlError && htmlData) {
          const htmlText = await htmlData.text();
          setPreviewHtml(htmlText);
          
          // Process the template using the same logic as ReviewDocument
          try {
            const processed = processUploadedTemplate(htmlText);
            setProcessedContent(processed);
          } catch (error) {
            console.error('Error processing HTML:', error);
          }
        }
      } 
      // If no admin uploaded HTML, try processed HTML
      else if (data.processed_html) {
        const { data: htmlData, error: htmlError } = await supabase.storage
          .from('template_html')
          .download(data.processed_html);
        if (!htmlError && htmlData) {
          const htmlText = await htmlData.text();
          setPreviewHtml(htmlText);
          
          // Process the template using the same logic as ReviewDocument
          try {
            const processed = processUploadedTemplate(htmlText);
            setProcessedContent(processed);
          } catch (error) {
            console.error('Error processing HTML:', error);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching template:', error);
      toast({
        title: "Error",
        description: "Failed to fetch template details",
        variant: "destructive",
      });
      navigate("/admin/review");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!template) return;
    try {
      const { data, error } = await supabase.storage
        .from('templates')
        .download(template.file_path);
      if (error) throw error;
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = template.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading template:', error);
      toast({
        title: "Error",
        description: "Failed to download template",
        variant: "destructive",
      });
    }
  };

  const handleUploadHTML = async () => {
    if (!template || !htmlFile) return;
    try {
      setProcessing(true);
      const filePath = `${template.user_id}/admin_${template.id}_${Date.now()}.html`;
      const { error: uploadError } = await supabase.storage
        .from('template_html')
        .upload(filePath, htmlFile, {
          upsert: true
        });
      if (uploadError) throw uploadError;
      const { error: updateError } = await supabase
        .from('templates')
        .update({
          admin_uploaded_html: filePath,
          admin_notes: adminNotes
        })
        .eq('id', template.id);
      if (updateError) throw updateError;
      const { data: htmlData, error: htmlError } = await supabase.storage
        .from('template_html')
        .download(filePath);
      if (!htmlError && htmlData) {
        const htmlText = await htmlData.text();
        setPreviewHtml(htmlText);
        
        // Process the template using the same logic as ReviewDocument
        try {
          const processed = processUploadedTemplate(htmlText);
          setProcessedContent(processed);
        } catch (error) {
          console.error('Error processing uploaded HTML:', error);
        }
      }
      toast({
        title: "Success",
        description: "HTML file uploaded successfully",
      });
      fetchTemplate();
    } catch (error) {
      console.error('Error uploading HTML:', error);
      toast({
        title: "Error",
        description: "Failed to upload HTML file",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
      setHtmlFile(null);
    }
  };

  const handleApprove = async () => {
    if (!template) return;
    try {
      setProcessing(true);
      const { error } = await supabase
        .from('templates')
        .update({
          needs_review: false,
          comments: adminNotes,
          admin_reviewed_at: new Date().toISOString(),
          admin_reviewed_by: (await supabase.auth.getUser()).data.user?.id,
          is_approved: true,
          admin_approval_status: 'approved',
          is_company_library: addToLibrary
        })
        .eq('id', template.id);
      if (error) throw error;
      toast({
        title: "Success",
        description: `Template approved successfully${addToLibrary ? ' and added to company library' : ''}`,
      });
      navigate("/admin/review");
    } catch (error) {
      console.error('Error approving template:', error);
      toast({
        title: "Error",
        description: "Failed to approve template",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!template) return;
    try {
      setProcessing(true);
      const { error } = await supabase
        .from('templates')
        .update({
          needs_review: false,
          admin_notes: adminNotes,
          admin_reviewed_at: new Date().toISOString(),
          admin_reviewed_by: (await supabase.auth.getUser()).data.user?.id,
          admin_approval_status: 'rejected',
          rejection_reason: adminNotes
        })
        .eq('id', template.id);
      if (error) throw error;
      toast({
        title: "Success",
        description: "Template rejected successfully",
      });
      navigate("/admin/review");
    } catch (error) {
      console.error('Error rejecting template:', error);
      toast({
        title: "Error",
        description: "Failed to reject template",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-64px)]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="text-center py-8">
        <h2 className="text-2xl font-bold">Template not found</h2>
        <Button className="mt-4" onClick={() => navigate("/admin/review")}>
          Back to Review Queue
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate("/admin/review")}>
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to Queue
          </Button>
          <h1 className="text-2xl font-bold">{template.filename}</h1>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline">
            {template.processing_status === 'completed' ? 'Ready for Review' : template.processing_status}
          </Badge>
          <Badge variant="outline">
            {template.admin_approval_status || 'Pending Review'}
          </Badge>
          <Button variant="outline" onClick={() => setShowDetails(true)}>
            View Details
          </Button>
        </div>
      </div>

      {/* Main Template Preview */}
      <Card className="min-h-[calc(100vh-250px)]">
        <CardHeader>
          <CardTitle>Template Preview</CardTitle>
        </CardHeader>
        <CardContent>
          {processedContent ? (
            <div className="border rounded-lg p-4 bg-white">
              <TemplateContent 
                content={processedContent.processedHtml}
                displayAsForm={false}
              />
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No preview available</p>
              <p className="text-sm">Upload an HTML file to preview the template</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Template Details Modal */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Template Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Uploaded</Label>
              <p className="text-sm text-muted-foreground">
                {format(new Date(template.created_at), "MMM d, yyyy h:mm a")}
              </p>
            </div>

            <div>
              <Label>Status</Label>
              <p className="text-sm text-muted-foreground">
                {template.processing_status}
                {template.processing_error && (
                  <span className="text-red-500 ml-2">({template.processing_error})</span>
                )}
              </p>
            </div>

            <div className="space-y-2">
              <Label>Admin Notes</Label>
              <Textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Add notes about the template..."
                className="min-h-[100px]"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox 
                id="company-library"
                checked={addToLibrary}
                onCheckedChange={(checked) => setAddToLibrary(checked === true)}
              />
              <label
                htmlFor="company-library"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Add to Company Library
              </label>
            </div>

            <div className="flex flex-col gap-2">
              <Button
                variant="outline"
                onClick={handleDownload}
                className="w-full"
              >
                <Download className="h-4 w-4 mr-2" />
                Download Original
              </Button>

              <div className="flex gap-2">
                <Input
                  type="file"
                  accept=".html"
                  onChange={(e) => setHtmlFile(e.target.files?.[0] || null)}
                  className="flex-1"
                />
                {htmlFile && (
                  <Button 
                    onClick={handleUploadHTML}
                    disabled={processing}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {processing ? 'Uploading...' : 'Upload'}
                  </Button>
                )}
              </div>
            </div>

            <div className="flex gap-2 mt-4">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" className="flex-1">
                    <XCircle className="h-4 w-4 mr-2" />
                    Reject
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Reject Template</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to reject this template? This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleReject}>
                      {processing ? 'Processing...' : 'Reject'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="default" className="flex-1">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approve
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Approve Template</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to approve this template? This will make it available to users.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleApprove}>
                      {processing ? 'Processing...' : 'Approve'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Full Screen Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl h-[80vh]">
          <DialogHeader>
            <DialogTitle>Template Preview</DialogTitle>
            <DialogDescription>
              Preview the template with sample data
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto">
            {processedContent && (
              <div className="border rounded-lg p-4 bg-white">
                <TemplateContent 
                  content={processedContent.processedHtml}
                  displayAsForm={false}
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
} 